import React from 'react'
import { styled } from '@mui/material/styles'
import { Avatar, Typography } from '@mui/material'

export interface FloatingMessage {
  id: string
  author: string
  content: string
  createdAt: number
  dismissed: boolean
}

interface FloatingMessagesProps {
  messages: FloatingMessage[]
  onDismiss: (id: string) => void
}

const FloatingMessages: React.FC<FloatingMessagesProps> = ({ messages, onDismiss }) => {
  const dateFormatter = React.useMemo(() => new Intl.DateTimeFormat('en', { timeStyle: 'short' }), [])

  return (
    <FloatingMessagesContainer>
      {messages.map((msg) => (
        <FloatingMessageBubble
          key={msg.id}
          isExiting={msg.dismissed}
          onClick={() => onDismiss(msg.id)}
        >
          <FloatingMessageHeader>
            <Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>
              {msg.author ? msg.author[0] : '?'}
            </Avatar>
            <FloatingMessageAuthor>{msg.author || 'Unknown'}</FloatingMessageAuthor>
          </FloatingMessageHeader>
          <FloatingMessageText>{msg.content}</FloatingMessageText>
          <FloatingMessageTime>
            {msg.createdAt ? dateFormatter.format(new Date(msg.createdAt)) : ''}
          </FloatingMessageTime>
        </FloatingMessageBubble>
      ))}
    </FloatingMessagesContainer>
  )
}

export default FloatingMessages

/* Styled components */
const FloatingMessagesContainer = styled('div')({
  position: 'absolute',
  top: '20px',
  left: '20px',
  right: '20px',
  zIndex: 15,
  pointerEvents: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  maxHeight: 'calc(100% - 120px)',
  overflow: 'hidden',
})

const FloatingMessageBubble = styled('div')<{ isExiting?: boolean }>(({ isExiting }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  borderRadius: '12px',
  padding: '12px 16px',
  maxWidth: '400px',
  minWidth: '200px',
  color: 'white',
  fontSize: '14px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
  cursor: 'pointer',
  pointerEvents: 'auto',
  position: 'relative',
  overflow: 'hidden',
  transition: isExiting
    ? 'opacity 0.3s ease-out, transform 0.3s ease-out'
    : 'opacity 0.4s ease-in, transform 0.4s ease-in',
  animation: isExiting ? 'slideOutUp 0.3s ease-out forwards' : 'slideInDown 0.4s ease-out',
  opacity: isExiting ? 0 : 1,
  transform: isExiting ? 'translateY(-20px)' : 'translateY(0)',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 25px rgba(0, 0, 0, 0.5)',
  },
  '@keyframes slideInDown': {
    from: {
      opacity: 0,
      transform: 'translateY(-30px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@keyframes slideOutUp': {
    from: {
      opacity: 1,
      transform: 'translateY(0)',
    },
    to: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
  },
}))

const FloatingMessageHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '6px',
})

const FloatingMessageAuthor = styled(Typography)({
  color: '#60a5fa',
  fontWeight: '600',
  fontSize: '13px',
})

const FloatingMessageText = styled('div')({
  color: '#e5e7eb',
  fontSize: '14px',
  lineHeight: '1.4',
  wordBreak: 'break-word',
})

const FloatingMessageTime = styled(Typography)({
  color: '#9ca3af',
  fontSize: '11px',
  marginTop: '4px',
}) 